import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

/**
 * MobileNumberStepper - Touch-friendly number input with +/- buttons
 *
 * @param {string} label - Label text above the stepper
 * @param {number} value - Current value
 * @param {function} onChange - Called with { value: newValue }
 * @param {number} min - Minimum value (default 0)
 * @param {number} max - Maximum value (default Infinity)
 * @param {number} step - Step increment (default 1)
 */
export default function MobileNumberStepper({ label, value, onChange, min = 0, max = Infinity, step = 1 }) {
    const handleDecrement = () => {
        const newVal = value - step;
        if (newVal >= min) {
            onChange({ value: newVal });
        }
    };

    const handleIncrement = () => {
        const newVal = value + step;
        if (newVal <= max) {
            onChange({ value: newVal });
        }
    };

    return (
        <Box sx={{ mt: 0 }}>
            {label && (
                <Typography
                    variant="body2"
                    sx={{
                        color: '#4b5263',
                        fontWeight: 500,
                        mb: 1,
                        fontSize: '0.875rem',
                    }}
                >
                    {label}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    background: 'rgba(0,0,0,0.02)',
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,0.08)',
                    p: 0.75,
                }}
            >
                <IconButton
                    onClick={handleDecrement}
                    disabled={value <= min}
                    sx={{
                        width: 36,
                        height: 36,
                        bgcolor: 'rgba(255,144,21,0.1)',
                        color: '#FF9015',
                        borderRadius: 2,
                        '&:hover': { bgcolor: 'rgba(255,144,21,0.2)' },
                        '&:active': { bgcolor: 'rgba(255,144,21,0.3)', transform: 'scale(0.95)' },
                        '&.Mui-disabled': { opacity: 0.3 },
                        transition: 'all 0.15s ease',
                    }}
                >
                    <RemoveIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <Typography
                    sx={{
                        fontSize: '1.15rem',
                        fontWeight: 700,
                        color: '#111318',
                        minWidth: 32,
                        textAlign: 'center',
                        userSelect: 'none',
                    }}
                >
                    {value}
                </Typography>
                <IconButton
                    onClick={handleIncrement}
                    disabled={value >= max}
                    sx={{
                        width: 36,
                        height: 36,
                        bgcolor: 'rgba(255,144,21,0.1)',
                        color: '#FF9015',
                        borderRadius: 2,
                        '&:hover': { bgcolor: 'rgba(255,144,21,0.2)' },
                        '&:active': { bgcolor: 'rgba(255,144,21,0.3)', transform: 'scale(0.95)' },
                        '&.Mui-disabled': { opacity: 0.3 },
                        transition: 'all 0.15s ease',
                    }}
                >
                    <AddIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>
        </Box>
    );
}
